import { formatDateToYMD, parseDateToMidnight } from '@/components/SearchTable/search-table-utils';
import { formatDateToOpenSearch } from '@/components/UI/DateRangerFilter/DateRanger.utils';
import {
  CREATED, DESC_ORDER, MAIN_CR, STATUS, STATUS_FILTER_SEPARATOR, USER_CODE, USER_ID, VERSION_DATE,
} from '@/helpers/constants';
import { operatorMapping } from '@/helpers/opensearch-utils';
import { FiltersOverview } from '@/types';
import { ColumnSorting, SortingDirection } from '@/types/TableListEditable';

const FILTER_MODEL_TYPE_TEXT = 'text';
const FILTER_MODEL_TYPE_SET = 'set';
const FILTER_MODEL_TYPE_DATE = 'date';
const FILTER_MODEL_TYPE_DATE_IN_RANGE = 'inRange';
const FILTER_MODEL_OPERATION_CONTAINS = operatorMapping.contains;
const FILTER_MODEL_TYPE_CONTAINS = 'contains';
const FILTER_MODEL_OPERATION_IN = operatorMapping.in;
const FILTER_MODEL_OPERATION_BETWEEN = operatorMapping.between;

export const mapColumn = (column: string) => (column === USER_CODE ? USER_ID : column);

export const buildFilterModel = (filterOverview: FiltersOverview | undefined): Record<string, unknown> => {
  if (!filterOverview) return {};

  return filterOverview.reduce((acc, { columnName, value }) => {
    if (columnName === STATUS && Array.isArray(value) && (value.length === 0 || typeof value[0] !== 'string')) {
      acc[columnName] = {
        filterType: FILTER_MODEL_TYPE_SET,
        values: (value as unknown as { key: string; type?: string[] }[]).map((item) => (
          `${item.type?.[0] ?? MAIN_CR}${STATUS_FILTER_SEPARATOR}${item.key}`
        )),
      };
    } else if ([VERSION_DATE, CREATED].includes(columnName) && Array.isArray(value) && value.length === 2) {
      const dateFrom = formatDateToYMD(value[0]);
      const dateTo = formatDateToYMD(value[1]);
      if (!dateFrom || !dateTo) return acc;
      acc[columnName] = {
        filterType: FILTER_MODEL_TYPE_DATE,
        type: FILTER_MODEL_TYPE_DATE_IN_RANGE,
        dateFrom,
        dateTo,
      };
    } else if (typeof value === 'string' && value !== '') {
      acc[columnName] = { filterType: FILTER_MODEL_TYPE_TEXT, type: FILTER_MODEL_TYPE_CONTAINS, filter: value };
    }

    return acc;
  }, {} as Record<string, unknown>);
};

export type FilterOverviewItem = { columnName: string; value: unknown; operation: string };

export const buildFilterOverview = (filterModel: Record<string, unknown>): FilterOverviewItem[] =>
  Object.entries(filterModel).reduce<FilterOverviewItem[]>((acc, [columnName, model]) => {
    const filter = model as { filter?: string; values?: string[]; dateFrom?: string; dateTo?: string };

    if (columnName === STATUS && Array.isArray(filter.values)) {
      acc.push({
        columnName,
        value: filter.values.map((item) => {
          const [type, key] = item.split(STATUS_FILTER_SEPARATOR);

          return { key, type: [type] };
        }),
        operation: FILTER_MODEL_OPERATION_IN,
      });

      return acc;
    }

    if ([VERSION_DATE, CREATED].includes(columnName)) {
      const startDate = parseDateToMidnight(filter.dateFrom);
      const endDate = parseDateToMidnight(filter.dateTo ?? filter.dateFrom);

      if (!startDate || !endDate) return acc;

      acc.push({
        columnName,
        value: [formatDateToOpenSearch(startDate, true), formatDateToOpenSearch(endDate, false)],
        operation: FILTER_MODEL_OPERATION_BETWEEN,
      });

      return acc;
    }

    acc.push({
      columnName,
      value: filter.filter || '',
      operation: FILTER_MODEL_OPERATION_CONTAINS,
    });

    return acc;
  }, []);

export const buildColumnSorting = (sortOverview?: { columnName: string; direction: string }[]): ColumnSorting[] =>
  sortOverview?.map((sort) => ({ columnName: sort.columnName, direction: sort.direction.toLowerCase() as SortingDirection }))
  || [{ columnName: CREATED, direction: DESC_ORDER }];

export const buildSortOverview = (columnSorting: ColumnSorting[]): { columnName: string; direction: string }[] =>
  columnSorting.map((sort) => ({ columnName: sort.columnName, direction: sort.direction.toUpperCase() }));
